<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import BlockUI from "@/components/BlockUI.vue";
import FormAlert from "@/components/FormAlert.vue";
import { startSsoLogin, useLoginPassword } from "@/queries/auth";
import { useLoginPrefsStore } from "@/stores/login-prefs-store";
import LoginCard from "./LoginCard.vue";

const route = useRoute();
const orgHint = route.query.org_hint as string | undefined;
const defaultPostLoginUrl = `${window.location.origin}/`;
const returnUrl = getPostLoginUrl(route.query.returnUrl);
const loginPrefs = useLoginPrefsStore();

const email = ref("");
const password = ref("");
const rememberEmail = ref(false);
const ssoRedirecting = ref(false);

const { loginPassword, loginPasswordPending, loginPasswordError, loginPasswordReset } = useLoginPassword();

const busy = computed(() => loginPasswordPending.value || ssoRedirecting.value);

const errorMessage = computed(() => {
  const err = loginPasswordError.value;
  if (!err) return "";
  if (err.problem.status === 401) {
    return "The email or password you entered is incorrect. Please try again.";
  }
  return err.userMessage;
});

function getPostLoginUrl(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string" || raw.length === 0) {
    return undefined;
  }

  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname === "/login") {
      return undefined;
    }

    return url.toString();
  } catch {
    return undefined;
  }
}

onMounted(() => {
  if (loginPrefs.savedEmail) {
    email.value = loginPrefs.savedEmail;
    rememberEmail.value = true;
  }
});

function redirectToSso() {
  ssoRedirecting.value = true;
  startSsoLogin(email.value.trim(), orgHint, returnUrl || defaultPostLoginUrl);
}

function handleSubmit() {
  loginPasswordReset();
  const trimmedEmail = email.value.trim();

  if (rememberEmail.value) {
    loginPrefs.rememberEmail(trimmedEmail);
  } else {
    loginPrefs.forgetEmail();
  }

  loginPassword(
    { email: trimmedEmail, password: password.value, orgHint },
    {
      onSuccess: () => {
        window.location.href = returnUrl || defaultPostLoginUrl;
      },
      onError: (err) => {
        if (err.problem.status === 401) password.value = "";
      },
    }
  );
}
</script>

<template>
  <BlockUI :active="busy">
    <LoginCard>
      <form @submit.prevent="handleSubmit">
        <FormAlert
          :error="errorMessage"
          class="my-3"
          @dismiss="loginPasswordReset"
        />

        <div class="mb-3">
          <input
            v-model="email"
            type="email"
            class="form-control"
            placeholder="Email"
            autocomplete="email"
            required
            :disabled="busy"
          />
        </div>

        <div class="mb-3">
          <input
            v-model="password"
            type="password"
            class="form-control"
            placeholder="Password"
            autocomplete="current-password"
            required
            :disabled="busy"
          />
        </div>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="form-check mb-0">
            <input
              id="rememberEmail"
              v-model="rememberEmail"
              class="form-check-input"
              type="checkbox"
              :disabled="busy"
            />
            <label class="form-check-label small text-secondary" for="rememberEmail">
              Remember me
            </label>
          </div>
          <router-link to="/forgot-password" class="small text-secondary">
            Forgot password?
          </router-link>
        </div>

        <button
          type="submit"
          class="bb-btn-primary-cta w-100"
          :disabled="busy"
        >
          Sign in
        </button>
      </form>

      <div class="divider my-4">
        <span class="text-secondary small">or</span>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary w-100"
        :disabled="busy"
        @click="redirectToSso"
      >
        <img
          src="/images/fullsail-logo-small.png"
          alt=""
          class="me-2 fullsail-btn-logo"
          onerror="this.style.display='none'"
        />
        Sign in with Full Sail Partners
      </button>

      <div class="text-center text-secondary small mt-4">
        Not yet registered? Please email
        <a href="mailto:support@fullsailpartners.com">
          support@fullsailpartners.com
        </a>
      </div>
    </LoginCard>
  </BlockUI>
</template>

<style lang="scss" scoped>
.divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background-color: var(--bs-border-color);
  }
}

.fullsail-btn-logo {
  height: 16px;
  width: auto;
  vertical-align: middle;
}
</style>
